import isEmpty from 'lodash/isEmpty';
import { logRailsEventToPDT } from '../PdtV2Utils';
import { EventDetailComponent, RisBaseEvent, RisEventValues } from './RisPdtV2Types';
import { COMMON_EVENT_TRACKING_CONTEXT, PDT_PNR_JOURNEY_ID_KEY } from '../CommonPdtConstants';
import { generateUUID } from '@mmt/navigation/src/util';
import { getDataFromAsynStorage, setDataToAsyncStorage } from 'apps/rails/src/Utils/RailsConstant';
import { pnrPdtEvents } from './RisPdtConstants';
import {
  CROSS_SELL_TYPES,
  CrossSellDataObj,
} from '../../../pages/RIS/PnrStatus/Components/PNRCrossSells/types';

interface risPdtProps {
  eventValue: RisEventValues;
  pnrDetails?: any;
  pnrCrossSell?: CrossSellDataObj | {};
}

export const logRisPdtEvent = async ({
  eventValue,
  pnrDetails,
  pnrCrossSell = {},
}: risPdtProps) => {
  try {
    const eventDetail = {
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...pnrPdtEvents(eventValue),
      booking_info: pnrDetailsMapper(pnrDetails) || null,
      components: pnrCrossSellMapper(pnrCrossSell) || null,
    };
    const loggerObj: RisBaseEvent = {
      page_context: {
        lob: 'rails',
        page_name: 'pnrDetails',
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: 61131,
        topic_name: 'mmt_ris_b2c_pdt_client_logging',
        journey_id: await getPnrJourneyId(),
      },
      event_detail: eventDetail,
    };
    logRailsEventToPDT(loggerObj);
    return loggerObj;
  } catch (error) {
    console.error('Error in logging RIS PDT event', error);
  }
};

const pnrDetailsMapper = (pnrDetailsInfo: any) => {
  if (isEmpty(pnrDetailsInfo)) {
    return null;
  }
  const trainDetails = pnrDetailsInfo?.TrainDetails;
  const pnrDetails = pnrDetailsInfo?.PnrDetails;
  const cityDetails = pnrDetailsInfo?.CityDetails;
  return {
    train_name: trainDetails?.Train?.Name || null,
    train_number: trainDetails?.Train?.Number || null,
    chart_prepared: trainDetails?.ChartPrepared ?? null,
    pnr_details: {
      pnr_number: pnrDetails?.Pnr || null,
      alert_eligible: pnrDetails?.AlertEligible ?? null,
      alert_set: pnrDetails?.AlertSet ?? null,
      pnr_current_status: pnrDetails?.PnrCurrentStatus || null,
      class: pnrDetails?.Class || null,
      quota: pnrDetails?.Quota || null,
    },
    from_station: {
      station: cityDetails?.SourceCity?.Name || null,
      code: cityDetails?.SourceCity?.Code || null,
      departure_date: pnrDetails?.SourceDoj?.Epoch || null,
    },
    to_station: {
      station: cityDetails?.DestinationCity?.Name || null,
      code: cityDetails?.DestinationCity?.Code || null,
      arrival_date: pnrDetails?.DestinationDoj?.Epoch || null,
    },
    fare_details: {
      booking_fare: pnrDetails?.FareDetails?.bookingFare || null,
      ticket_fare: pnrDetails?.FareDetails?.ticketFare || null,
    },
    passenger_details: pnrPassengerDetailsMapper(
      pnrDetailsInfo?.PassengerDetails?.PassengerStatus || [],
    ),
  };
};

const pnrPassengerDetailsMapper = (passengers: any) => {
  if (passengers?.length === 0) {
    return null;
  }
  const passengerDetails = passengers?.map((pax: any) => {
    return {
      number: pax?.Number || null,
      booking_status: {
        berth_number: pax?.BookingBerthNo || null,
        coach: pax?.BookingCoachId || null,
        status: pax?.BookingStatus || null,
      },
      current_status: {
        berth_number: pax?.CurrentBerthNo || null,
        coach: pax?.CurrentCoachId || null,
        status: pax?.CurrentStatus || null,
      },
    };
  });
  return passengerDetails;
};

const pnrCrossSellMapper = (crossSellInfo: CrossSellDataObj | {}) => {
  if (isEmpty(crossSellInfo)) {
    return null;
  }
  const components: EventDetailComponent[] = [];
  switch (crossSellInfo?.type) {
    case CROSS_SELL_TYPES.CONFIRMED_OPTIONS:
      components.push(alternateAvailabilityMapper(crossSellInfo));
      break;
    case CROSS_SELL_TYPES.RETURN_TICKETS:
      components.push(returnTripAvailabilityMapper(crossSellInfo));
      break;
    case CROSS_SELL_TYPES.BUS:
      components.push(busCrossSellMapper(crossSellInfo));
      break;
    default:
      break;
  }
  return components;
};

const alternateAvailabilityMapper = (alternateAvailabilityInfo: any) => {
  return {
    id: CROSS_SELL_TYPES.CONFIRMED_OPTIONS,
    component_type:
      alternateAvailabilityInfo?.isFromBottomSheet === undefined
        ? null
        : alternateAvailabilityInfo?.isFromBottomSheet
        ? 'BottomSheet'
        : 'Inline',
    lob: 'rails',
    product_details:
      {
        number: alternateAvailabilityInfo?.data?.trainNumber || null,
        name: alternateAvailabilityInfo?.data?.trainName || null,
        from: {
          code: alternateAvailabilityInfo?.data?.frmStnCode || null,
          name: alternateAvailabilityInfo?.data?.frmStnName || null,
          departure_date: alternateAvailabilityInfo?.data?.departureDate || null,
          departure_time: alternateAvailabilityInfo?.data?.departureTime || null,
          },
        to: {
          code: alternateAvailabilityInfo?.data?.toStnCode || null,
          name: alternateAvailabilityInfo?.data?.toStnName || null,
          arrival_date: alternateAvailabilityInfo?.data?.arrivalDate || null,
          arrival_time: alternateAvailabilityInfo?.data?.arrivalTime || null,
          },
          price_details: {
            coupon: {
              coupon_id: alternateAvailabilityInfo?.coupon?.code || null,
              coupon_amount: alternateAvailabilityInfo?.coupon?.amount || null,
            },
          },
        catalog_list:
          alternateAvailabilityInfo?.data?.classAvailabilityList?.map((classInfo: any) => {
              const tbsInfo: any = classInfo?.tbsAvailability;
              return {
                class_name: tbsInfo?.classType || null,
                quota: tbsInfo?.quota || null,
                price: {
                  total_amount: parseFloat(tbsInfo?.totalFare) || null,
                },
                status: tbsInfo?.availablityStatus || null,
                date: tbsInfo?.availablityDate || null,
                availability_last_updated: tbsInfo?.lastUpdatedOn || null,
              };
            }) || null,
      } || null,
  };
};

const returnTripAvailabilityMapper = (returnTripAvailabilityInfo: any) => {
  return {
    id: CROSS_SELL_TYPES.RETURN_TICKETS,
    component_type:
      returnTripAvailabilityInfo?.isFromBottomSheet === undefined
        ? null
        : returnTripAvailabilityInfo?.isFromBottomSheet
        ? 'BottomSheet'
        : 'Inline',
    lob: 'rails',
    content_details:
      returnTripAvailabilityInfo?.data?.map((tripDetails: any, index: number) => {
        return {
          type: 'RETURN_TICKETS',
          from: {
            code: returnTripAvailabilityInfo?.searchParams?.sourceCode || null,
            name: returnTripAvailabilityInfo?.searchParams?.sourceName || null,
            departure_date: tripDetails?.date || null,
          },
          to: {
            code: returnTripAvailabilityInfo?.searchParams?.destinationCode || null,
            name: returnTripAvailabilityInfo?.searchParams?.destinationName || null,
          },
          coupon: {
            coupon_id: returnTripAvailabilityInfo?.coupon?.code || null,
            coupon_amt: returnTripAvailabilityInfo?.coupon?.amount || null,
          },
          display_text: tripDetails?.statusText || null,
          rank: index + 1,
        };
      }) || null,
  };
};

const busCrossSellMapper = (busCrossSellInfo: any) => {
  return {
    id: CROSS_SELL_TYPES.BUS,
    component_type:
      busCrossSellInfo?.isFromBottomSheet === undefined
        ? null
        : busCrossSellInfo?.isFromBottomSheet
        ? 'BottomSheet'
        : 'Inline',
    lob: 'rails',
    product_details:
      busCrossSellInfo?.data?.map((busInfo: any, index: number) => {
        return {
          type: busInfo?.node?.type || null,
          name: busInfo?.node?.operatorName || null,
          category: busInfo?.node?.attributes?.type || null,
          from: {
            code: busCrossSellInfo?.searchParams?.sourceCode || null,
            name: busCrossSellInfo?.searchParams?.sourceName || null,
            departure_time: busInfo?.node?.journeyInfo?.departureTimeStamp || null,
          },
          to: {
            code: busCrossSellInfo?.searchParams?.destinationCode || null,
            name: busCrossSellInfo?.searchParams?.destinationName || null,
            arrival_time: busInfo?.node?.journeyInfo?.arrivalTimeStamp || null,
          },
          price: busInfo?.node?.fareDetails?.displayPrice || null,
          price_details:
            {
              coupon_id: busCrossSellInfo?.coupon?.code || null,
              coupon_amt: busCrossSellInfo?.coupon?.amount || null,
            } || null,
          star_rating: busInfo?.node?.extraInfo?.overallRating ?? null,
          rank: index + 1,
        };
      }) || null,
  };
};

const getPnrJourneyId = async () => {
  const journeyId = (await getDataFromAsynStorage(PDT_PNR_JOURNEY_ID_KEY)) ?? generateUUID();
  setDataToAsyncStorage(PDT_PNR_JOURNEY_ID_KEY, journeyId);
  return journeyId;
};
